import KatalLogger, { type LoggerConfig } from '@amzn/katal-logger';
import sentryFetch from '@amzn/sentry-fetch';
import config from 'src/config';
import { applicationMetricsPublisher, METRIC } from './metrics';

const loggerUrl = `${config.loggerBaseUrl}/v1/log`;
const allowKatalUsageMetrics = false;
const localLoggerConfig: LoggerConfig = {
  recordMetrics: allowKatalUsageMetrics,
  logToConsole: true,
};

const prodLoggerConfig: LoggerConfig = {
  recordMetrics: allowKatalUsageMetrics,
  url: loggerUrl,
  xhrWithCredentials: true,
  maxLogsPerBatch: process.env.NODE_ENV === 'test' ? 0 : undefined,

  // Required since we use Katal's generated Midway interceptor
  sendToBackendOverride: async (body) => {
    try {
      await sentryFetch(loggerUrl, {
        method: 'POST',
        credentials: 'include',
        body,
      });
    } catch (err) {
      console.error(err);
    }
  },
};

type Context = Record<string, unknown>;

class LeelaUILogger extends KatalLogger {
  constructor () {
    super(
      process.env.NODE_ENV === 'development'
        ? localLoggerConfig
        : prodLoggerConfig
    );

    // Error catchall
    this.addErrorListener((_error: Error | undefined) => {
      applicationMetricsPublisher.publishCounter(METRIC.Error, 1);

      return true;
    });
  }

  /**
   * Add dynamic data (such as href) to every log event.
   *
   * @param [context] - Existing context to enrich
   */
  private enrichContext (
    context?: Record<string, unknown>
  ): Record<string, unknown> {
    return {
      ...context,
      href: location.href,
    };
  }

  override debug (message: string, context?: Context): Promise<void>;
  override debug (
    message: string,
    error: Error,
    context?: Context
  ): Promise<void>;
  override debug (
    message: string,
    error?: Error | Context,
    context?: Context
  ): Promise<void> {
    if (error instanceof Error) {
      return super.debug(message, error, this.enrichContext(context));
    } else {
      return super.debug(message, this.enrichContext(error));
    }
  }

  override info (message: string, context?: Context): Promise<void>;
  override info (
    message: string,
    error: Error,
    context?: Context
  ): Promise<void>;
  override info (
    message: string,
    error?: Error | Context,
    context?: Context
  ): Promise<void> {
    if (error instanceof Error) {
      return super.info(message, error, this.enrichContext(context));
    } else {
      return super.info(message, this.enrichContext(error));
    }
  }

  override warn (message: string, context?: Context): Promise<void>;
  override warn (
    message: string,
    error: Error,
    context?: Context
  ): Promise<void>;
  override warn (
    message: string,
    error?: Error | Context,
    context?: Context
  ): Promise<void> {
    if (error instanceof Error) {
      return super.warn(message, error, this.enrichContext(context));
    } else {
      return super.warn(message, this.enrichContext(error));
    }
  }

  override error (message: string, context?: Context): Promise<void>;
  override error (
    message: string,
    error: Error,
    context?: Context
  ): Promise<void>;
  override error (
    message: string,
    error?: Error | Context,
    context?: Context
  ): Promise<void> {
    if (error instanceof Error) {
      return super.error(message, error, this.enrichContext(context));
    } else {
      return super.error(message, this.enrichContext(error));
    }
  }

  override fatal (message: string, context?: Context): Promise<void>;
  override fatal (
    message: string,
    error: Error,
    context?: Context
  ): Promise<void>;
  override fatal (
    message: string,
    error?: Error | Context,
    context?: Context
  ): Promise<void> {
    if (error instanceof Error) {
      return super.fatal(message, error, this.enrichContext(context));
    } else {
      return super.fatal(message, this.enrichContext(error));
    }
  }
}

export const Logger = new LeelaUILogger();
